import { retrieveRawLaunchParams } from '@telegram-apps/sdk-react';

/**
 * Запасная копия данных запуска Telegram (`tgWebAppData` и соседи) — на
 * случай, когда система выгрузила мини-апп в фоне.
 *
 * Telegram кладёт их в `#hash` адреса только при открытии. SDK ищет их в
 * текущем адресе, в адресе первой загрузки (`performance`) и в
 * `sessionStorage`. `BrowserRouter` при первом же переходе (`/` → `/home`)
 * переписывает адрес без hash, а когда телефон выгружает свёрнутый Telegram (полчаса в
 * TikTok), WebView поднимается заново на последнем адресе и с пустым
 * `sessionStorage`. Все три источника пусты — и вместо приложения экран
 * «This app runs inside Telegram».
 *
 * `localStorage` переживает выгрузку. Подписанный initData бэкенд принимает
 * сутки (`INIT_DATA_MAX_AGE_SECONDS`), каждое новое открытие копию обновляет.
 */
const STORAGE_KEY = 'tma:launch-params';

/** Запомнить данные этого запуска — после того как SDK их нашёл. */
export function backupLaunchParams(): void {
  try {
    localStorage.setItem(STORAGE_KEY, retrieveRawLaunchParams());
  } catch {
    // Нет данных или нет хранилища — запасной копии просто не будет.
  }
}

/**
 * Вернуть сохранённые данные в адрес, если их там нет, — SDK прочитает их
 * оттуда как при обычном открытии. Только внутри клиента Telegram: в обычном
 * браузере (тот же origin) копия от прошлого запуска не должна открывать
 * приложение вместо экрана со ссылкой в бота.
 *
 * @returns удалось ли что-то восстановить.
 */
export function restoreLaunchParams(): boolean {
  if (!insideTelegramClient()) return false;
  let saved: string | null = null;
  try {
    saved = localStorage.getItem(STORAGE_KEY);
  } catch {
    return false;
  }
  if (!saved) return false;
  // `replaceState`, а не `location.hash =`: без новой записи в истории и без
  // `hashchange`, роутер его не заметит. Hash роутеру не нужен (см. `AppRouter`).
  history.replaceState(history.state, '', `${location.pathname}${location.search}#${saved}`);
  return true;
}

/**
 * Мост клиента Telegram виден и без данных запуска: Android и iOS
 * подкладывают `TelegramWebviewProxy`, Desktop — `external.notify`, веб-версия
 * открывает мини-апп во фрейме.
 */
function insideTelegramClient(): boolean {
  const w = window as Window & { TelegramWebviewProxy?: unknown; external?: { notify?: unknown } };
  return (
    w.TelegramWebviewProxy != null || typeof w.external?.notify === 'function' || w.parent !== w
  );
}
